/**
 * <shortcut-box-options> — PLP shortcut ("red box") option grid.
 *
 * AAS-331 (mobile only): the two-column layout uses "short" half-width buttons.
 * On mobile there is no hover to reveal a truncated label and labels show in
 * full without an ellipsis, so if ANY label is too long to sit on one line in a
 * short button, every button collapses to a single full-width column.
 *
 * Desktop keeps the CSS-only hover-open behaviour and this element is inert
 * there. Detection measures real layout, so it stays correct across
 * breakpoints, orientation changes, web-font loading and facet re-renders.
 */
class ShortcutBoxOptions extends HTMLElement {
  connectedCallback() {
    this.options = Array.from(this.querySelectorAll('.shortcut-box__option'));
    if (this.options.length === 0) return;

    this._lastWidth = 0;
    this._evaluate = this._evaluate.bind(this);

    // Re-measure after web fonts settle — metrics shift when the real font
    // swaps in and can change how many lines a label wraps to.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this._evaluate);
    }

    this._resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      // Only width changes (breakpoint cross / rotation) can change the outcome;
      // ignore the height change our own class toggle causes, to avoid a loop.
      if (width === this._lastWidth) return;
      this._lastWidth = width;
      this._evaluate();
    });
    this._resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (this._resizeObserver) this._resizeObserver.disconnect();
  }

  _evaluate() {
    const FORCE_SINGLE = 'shortcut-box__options--force-single';

    // Short buttons only exist in the two-column layout, and the collapse is a
    // mobile-only rule. Anywhere else, make sure the flag is cleared.
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    const isTwoCol = this.classList.contains('shortcut-box__options--two-col');
    if (!isMobile || !isTwoCol) {
      this.classList.remove(FORCE_SINGLE);
      return;
    }

    // Measure at the two-column (half-width) baseline.
    this.classList.remove(FORCE_SINGLE);

    let anyTooLong = false;
    for (const option of this.options) {
      const text =
        option.querySelector('.shortcut-box__option-text-full') ||
        option.querySelector('.shortcut-box__option-text');
      if (!text) continue;
      const cs = getComputedStyle(text);
      let lineHeight = parseFloat(cs.lineHeight);
      if (Number.isNaN(lineHeight)) {
        lineHeight = parseFloat(cs.fontSize) * 1.2;
      }
      // More than one line means the label does not fit the short button.
      if (text.scrollHeight > lineHeight * 1.5) {
        anyTooLong = true;
        break;
      }
    }

    if (anyTooLong) {
      this.classList.add(FORCE_SINGLE);
    }
  }
}

customElements.define('shortcut-box-options', ShortcutBoxOptions);
